import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileUploader } from "react-drag-drop-files";

import { ModalPlayerVerificationTabs } from "configs/types";
import {
  getRiskKycDocumentsByUser,
  getRiskKycDocumentsOptions,
  reviewRiskKycDocument,
} from "store/risk/kycDocuments/slice";
import { useAppDispatch, useAppSelector } from "store";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Options } from "components/table/components/Options/Options";
import { useContentPreviewModal } from "modals/shared/ContentPreviewModal/useContentPreviewModal";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { formatDate, formatTime, useTimeFormat } from "helpers/timeFormat";
import ProfileDataTab, { ProfileDataAction } from "./ProfileDataTab";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import { handleApiError } from "helpers/handleApiError";
import { showNotification, NotificationTypes } from "store/notifications/slice";
import Loader from "components/global/Loader";

const fileTypes = ["JPG", "JPEG", "PNG", "PDF", "WEBP"];

const documentTitles: Record<string, string> = {
  passport: "Passport",
  idCard: "ID Card",
  driverLicense: "Driver License",
  driversLicense: "Driver License",
  proofOfAddress: "Proof of Address",
  paymentMethod: "Payment Method",
};

const isDriverLicenseTab = (tab: string | null) =>
  tab === "driverLicense" || tab === "driversLicense";

const belongsToActiveTab = (documentType: string, activeTab: string | null) =>
  documentType === activeTab ||
  (isDriverLicenseTab(documentType) && isDriverLicenseTab(activeTab));

const statusBadgeClasses: Record<string, string> = {
  missing: "warning",
  requiresreview: "warning",
  requiresreupload: "warning",
};

const reviewStatusOptions = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "expired", label: "Expired" },
  { value: "requiresReview", label: "Requires Review" },
];

interface Props {
  files: Record<string, File | null>;
  setFiles: React.Dispatch<React.SetStateAction<Record<string, File | null>>>;
  onProfileDataActionChange: (action: ProfileDataAction | null) => void;
}

const ListVerificationTab: React.FC<Props> = ({
  files,
  setFiles,
  onProfileDataActionChange,
}) => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const entityId = searchParams.get("entityId");
  const {
    list,
    isListFetched,
    options,
    isOptionsFetched,
    processingDocumentId,
  } = useAppSelector((state) => state.riskKycDocuments);

  const { t } = useTranslation();
  const timeFormat = useTimeFormat();
  const { openPreview } = useContentPreviewModal();
  const navigate = useNavigate();
  const verificationTabFromUrl = searchParams.get("verificationTab");
  const permissions = usePermissionHelper();
  const kycPermissions = usePermissionHelper("/risk/kyc/documents");
  const canCreate = permissions?.create ?? false;
  const canUpdateDocumentStatus = kycPermissions?.update ?? false;

  const [activeTab, setActiveTab] =
    useState<ModalPlayerVerificationTabs | null>(
      (verificationTabFromUrl as ModalPlayerVerificationTabs) ||
        ModalPlayerVerificationTabs.profileData,
    );
  useEffect(() => {
    if (
      activeTab === ModalPlayerVerificationTabs.profileData &&
      !verificationTabFromUrl &&
      entityId
    ) {
      navigate(
        `${location.pathname}?activeModal=player&entityId=${entityId}&tab=verification&verificationTab=profileData`,
        { replace: true },
      );
    }
  }, [
    activeTab,
    verificationTabFromUrl,
    entityId,
    navigate,
    location.pathname,
  ]);

  useEffect(() => {
    if (!isOptionsFetched) {
      dispatch(getRiskKycDocumentsOptions());
    }
  }, [dispatch, isOptionsFetched]);

  useEffect(() => {
    if (!entityId || activeTab === ModalPlayerVerificationTabs.profileData)
      return;

    dispatch(getRiskKycDocumentsByUser(Number(entityId)));
  }, [activeTab, dispatch, entityId]);

  const currentDocuments = list.filter((doc) =>
    belongsToActiveTab(doc.documentType, activeTab),
  );

  const getStatusDisplay = (status: string) => {
    const statusKey = status.toLowerCase();
    const option = options?.documentStatuses.find(
      (item) => item.value.toLowerCase() === statusKey,
    );

    return {
      label: option?.title ?? status,
      badgeClass: statusBadgeClasses[statusKey] ?? statusKey,
    };
  };

  const currentSides =
    activeTab === ModalPlayerVerificationTabs.idCard ||
    isDriverLicenseTab(activeTab)
      ? ["front", "back"]
      : ["single"];

  const frontDocument = currentDocuments.find(
    (doc) => doc.documentSide === "front",
  );

  const backDocument = currentDocuments.find(
    (doc) => doc.documentSide === "back",
  );

  const handleFileChange = (side: string, file: File) => {
    setFiles((prev) => ({
      ...prev,
      [side]: file,
    }));
  };

  const isTwoSided =
    activeTab === ModalPlayerVerificationTabs.idCard ||
    isDriverLicenseTab(activeTab);

  const primarySide = isTwoSided ? "front" : "single";

  const primaryDocument = currentDocuments.find(
    (doc) => doc.documentSide === primarySide,
  );

  return (
    <div className="player-content-wrapper">
      <div className="game-modal-tabs small verification-tabs">
        <div
          className={`tab-item ${
            activeTab === ModalPlayerVerificationTabs.profileData
              ? "active"
              : ""
          }`}
          onClick={() => {
            setFiles({});
            setActiveTab(ModalPlayerVerificationTabs.profileData);
            navigate(
              `${location.pathname}?activeModal=player&entityId=${entityId}&tab=verification&verificationTab=profileData`,
            );
          }}
        >
          Profile Data
        </div>
        {options?.documentTypes
          .filter((el) => el.value !== ModalPlayerVerificationTabs.profileData)
          .map((el) => (
            <div
              className={`tab-item ${activeTab === el.value ? "active" : ""}`}
              key={el.value}
              onClick={() => {
                setFiles({});

                setActiveTab(el.value as ModalPlayerVerificationTabs);

                navigate(
                  `${location.pathname}?activeModal=player&entityId=${entityId}&tab=verification&verificationTab=${el.value}`,
                );
              }}
            >
              {el.title}
            </div>
          ))}
      </div>
      <div className="verification-wrapper">
        {activeTab === ModalPlayerVerificationTabs.profileData && entityId && (
          <ProfileDataTab
            userId={Number(entityId)}
            onActionChange={onProfileDataActionChange}
          />
        )}
        {activeTab !== ModalPlayerVerificationTabs.profileData &&
          !isListFetched && <ModalTabSkeleton />}
        {activeTab !== ModalPlayerVerificationTabs.profileData &&
          isListFetched &&
          activeTab && (
            <>
              <div
                className={`verification-content ${
                  currentSides.length === 1 ? "single" : ""
                }`}
              >
                <div className={`item ${files[primarySide] ? "uploadet" : ""}`}>
                  <div className="label">
                    {isTwoSided ? t("frontSide") : t("single")}
                  </div>
                  {canCreate && (
                    <FileUploader
                      name="file"
                      handleChange={(file: File) =>
                        handleFileChange(primarySide, file)
                      }
                      types={fileTypes}
                      uploadedLabel={
                        files[primarySide]
                          ? `${t("fileSelected")}: ${files[primarySide]?.name}`
                          : `${t("uploadOrDropFileHere")}`
                      }
                    />
                  )}
                  {primaryDocument && (
                    <div className="uploaded-file">
                      <div className="uploaded-file-info">
                        <div className="uploaded-file-photo">
                          <img
                            src="/sidebar/document.svg"
                            alt="document"
                            className="uploaded-file-icon"
                          />
                        </div>
                        <div className="uploaded-file-content">
                          <div className="uploaded-file-name">
                            {primaryDocument.fileName ||
                              documentTitles[primaryDocument.documentType] ||
                              primaryDocument.documentType}
                          </div>
                          <div className="uploaded-file-size">
                            {primaryDocument.fileSize != null && (
                              <span>
                                {(primaryDocument.fileSize / 1024).toFixed(1)}{" "}
                                KB
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="uploaded-file-status">
                        <span>
                          {getStatusDisplay(primaryDocument.status).label}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                {currentSides.includes("back") && (
                  <div className={`item ${files.back ? "uploadet" : ""}`}>
                    <div className="label">{t("backSide")}</div>
                    {canCreate && (
                      <FileUploader
                        name="file"
                        handleChange={(file: File) =>
                          handleFileChange("back", file)
                        }
                        types={fileTypes}
                        uploadedLabel={
                          files.back
                            ? `${t("fileSelected")}: ${files.back?.name}`
                            : `${t("uploadOrDropFileHere")}`
                        }
                      />
                    )}
                    {backDocument && (
                      <div className="uploaded-file">
                        <div className="uploaded-file-info">
                          <div className="uploaded-file-photo">
                            <img
                              src="/sidebar/document.svg"
                              alt="document"
                              className="uploaded-file-icon"
                            />
                          </div>
                          <div className="uploaded-file-content">
                            <div className="uploaded-file-name">
                              {backDocument.fileName ||
                                documentTitles[backDocument.documentType] ||
                                backDocument.documentType}
                            </div>
                            <div className="uploaded-file-size">
                              {backDocument.fileSize != null && (
                                <span>
                                  {(backDocument.fileSize / 1024).toFixed(1)} KB
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="uploaded-file-status">
                          <span>
                            {getStatusDisplay(backDocument.status).label}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="verification-history">
                <div className="inner-table-wrapper">
                  <table>
                    <thead>
                      <tr>
                        <th>{t("side")}</th>
                        <th>{t("document")}</th>
                        <th>{t("status")}</th>
                        <th>{t("source")}</th>
                        <th>{t("origin")}</th>
                        <th>{t("updatedAt")}</th>
                        <th>{t("options")}</th>
                      </tr>
                    </thead>

                    <tbody>
                      {currentDocuments.map((doc) => (
                        <tr
                          key={doc.id}
                          className={
                            processingDocumentId === doc.id
                              ? "modal-table-processing-row"
                              : ""
                          }
                        >
                          <td>{doc.documentSide}</td>

                          <td>
                            <div
                              className="file-name-cell"
                              title={
                                documentTitles[doc.documentType] ||
                                doc.documentType
                              }
                            >
                              {documentTitles[doc.documentType] ||
                                doc.documentType}
                            </div>
                          </td>

                          <td>
                            <div
                              className={`table-status-dropdown table-status-dropdown--kyc table-status-dropdown--${doc.status.toLowerCase()}`}
                            >
                              <DefaultDropdown
                                value={doc.status}
                                options={reviewStatusOptions}
                                isSmall
                                useFixedMenu
                                disabled={!canUpdateDocumentStatus}
                                onSelect={async (status) => {
                                  if (status === doc.status) return;
                                  try {
                                    await dispatch(
                                      reviewRiskKycDocument({
                                        documentId: doc.id,
                                        status: status as
                                          | "pending"
                                          | "approved"
                                          | "rejected"
                                          | "expired"
                                          | "requiresReview",
                                      }),
                                    ).unwrap();
                                    dispatch(
                                      showNotification({
                                        title: t("success"),
                                        message: t("entityUpdateSuccess"),
                                        type: NotificationTypes.success,
                                      }),
                                    );
                                  } catch (error) {
                                    handleApiError({ error, t, dispatch });
                                  }
                                }}
                              />
                            </div>
                          </td>

                          <td>
                            {doc.source === "provider"
                              ? t("provider")
                              : doc.source === "manual"
                                ? t("manual")
                                : "Player"}
                          </td>

                          <td>
                            {doc.source === "provider"
                              ? t("verification")
                              : t("backOffice")}
                          </td>

                          <td className="date">
                            <span>{formatDate(doc.updatedAt)}</span>
                            <span>{formatTime(doc.updatedAt, timeFormat)}</span>
                          </td>

                          <td>
                            <Options
                              id={doc.id}
                              hasView
                              hasEdit={false}
                              hasDelete={false}
                              onView={() =>
                                openPreview(Number(entityId), doc.id)
                              }
                            />
                          </td>

                          {processingDocumentId === doc.id && (
                            <div className="modal-table-processing-spinner">
                              <Loader />
                            </div>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
      </div>
    </div>
  );
};

export default ListVerificationTab;
